import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const H2_CLS = "font-serif text-2xl md:text-3xl text-ink leading-tight tracking-tight mt-14 mb-5";
const H3_CLS = "font-serif text-xl md:text-2xl text-ink leading-tight tracking-tight mt-10 mb-3";
const P_CLS = "my-5 leading-[1.85]";
const LIST_CLS = "my-5 space-y-3";
const LI_CLS = "flex gap-3";
const DASH = (
  <span className="font-serif text-ember text-xl leading-none mt-1 flex-shrink-0">—</span>
);

function Sidebar() {
  return (
    <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start">
      <div className={OVERLINE_CLS}>About</div>
      <div className="mt-3 font-accent text-sm text-ink-muted">Who runs this site</div>
      <div className="mt-6 h-px w-12 bg-ember/60" />
      <p className="mt-6 text-sm text-ink-muted leading-relaxed font-light">
        There is no organisation behind this site and no funding from anyone. Nothing is here because someone paid for it to be.
      </p>
      <p className="mt-4 text-sm text-ink-muted leading-relaxed font-light">
        One person reads the inbox. Allow a week for a reply.
      </p>
      <LastReviewed path="/about" className="mt-6" />
    </aside>
  );
}

function Belief({ heading, children }) {
  return (
    <div className="border-l-4 border-ember pl-6 py-1 my-6">
      <p className="font-serif text-lg md:text-xl text-ink leading-snug not-italic">
        <strong className="text-ink">{heading}</strong> {children}
      </p>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageMeta
        title="About this site — You Are Not Alone"
        description="Who runs this site, why it exists, what it does and does not do, how it is written and checked, and how to get in touch. Independent, one person, no funding."
        path="/about"
      />
      <PageHeader
        overline="About this site"
        title="One person, no funding, no organisation."
        lede="This site exists because good information about the child protection system in England is scattered, and often written for lawyers rather than families. Here is who runs it, why, and what you can expect from it."
      />

      <article className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <Link
          to="/"
          data-testid="about-back-link"
          className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink mb-10"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back to home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Sidebar />
          <div className="lg:col-span-9 max-w-3xl text-base md:text-lg text-ink-muted font-light leading-[1.85]">

            <h2 className={H2_CLS}>Who runs it</h2>
            <p className={P_CLS}>
              This site is run by one person: a semi-retired professional with a working background in insurance, where the job was to weigh evidence, test disputed accounts against the record, and reach findings that could stand up to scrutiny.
            </p>
            <p className={P_CLS}>
              It is not a charity, a law firm, or a campaign group with staff. There is no organisation behind it and no funding from anyone — no grants, no donations, no advertising, no paid links in the support directory. The hosting costs are paid personally and they are small. Nothing on this site is here because someone paid for it to be.
            </p>
            <p className={P_CLS}>
              You can reach the person who runs it at{" "}
              <a
                href="mailto:info@truth4parents.com"
                className="text-river underline underline-offset-4 hover:text-ember"
              >
                info@truth4parents.com
              </a>
              . Messages are read by one person and no one else.
            </p>

            <h2 className={H2_CLS}>Why it exists</h2>
            <p className={P_CLS}>
              This site was built after close involvement with child protection proceedings in England — not as a professional, but as someone drawn into the process and left with questions about how it works.
            </p>
            <p className={P_CLS}>
              What followed was months of reading: judgments, statutory guidance, inspection reports, parliamentary committee evidence, and a great deal of what families themselves have written online. Two things stood out.
            </p>
            <p className={P_CLS}>
              The first was how much good information exists and how badly it is organised. The law is public. The guidance is public. Much of the case law is now published. But it is scattered across a dozen sites written for lawyers, and a parent who has just had a phone call from a social worker has neither the time nor the state of mind to assemble it.
            </p>
            <p className={P_CLS}>
              The second was the sheer number of families writing about the same experiences — often far harder than the one that prompted this site — into spaces where nobody with any power was reading. There are forums, comment threads and social media groups full of people describing similar things happening to them, and very little connecting that to anyone who could act on it.
            </p>
            <p className={P_CLS}>
              Senior judges, MPs and academics have said, publicly and repeatedly, that there are problems with how the family justice system operates and how little of it can be seen from outside. What has been slower to arrive is a way for ordinary families to understand what is happening to them while it is happening.
            </p>
            <p className={P_CLS}>
              This site is a small attempt at the second part.
            </p>

            <h2 className={H2_CLS}>What we believe</h2>

            <Belief heading="Children must be protected.">
              Some children are genuinely at risk and some do need to be removed from their families. Social workers and the family courts do difficult, necessary work, usually with too little time and too few resources, and often get it right. Anyone who tells you otherwise is not being straight with you.
            </Belief>

            <Belief heading="A system that cannot be seen cannot be trusted.">
              The family court operates largely in private, for reasons that are sound — children are entitled to their privacy. But privacy for the child has become, in practice, anonymity for the institutions. Decisions of enormous consequence are made about families by people whose reasoning is rarely published, whose accuracy is rarely tested from outside, and who face little consequence when they get something materially wrong.
            </Belief>

            <Belief heading="Being wrong should be correctable.">
              When a statement about a family turns out to be inaccurate, there should be a clear, quick and well-known route to have it corrected — including in the court record. At present that route is slow, obscure, and largely unknown to the people who need it.
            </Belief>

            <Belief heading="Understanding is not the same as fighting.">
              Nothing here is intended to help anyone obstruct a child protection enquiry or evade scrutiny. Parents who understand the process tend to engage with it better, not worse. That is good for their children, and it is good for the professionals working with them.
            </Belief>

            <Belief heading="Anger is not a strategy.">
              There is a great deal of understandable rage in this subject, and very little of it changes anything. This site is deliberately measured, because measured is what gets read by the people who can make decisions.
            </Belief>

            <h2 className={H2_CLS}>What this site does and does not do</h2>

            <h3 className={H3_CLS}>It does</h3>
            <ul className={LIST_CLS}>
              <li className={LI_CLS}>{DASH}<span>Explain the child protection process in plain English.</span></li>
              <li className={LI_CLS}>{DASH}<span>Translate the terminology into language you can read out loud.</span></li>
              <li className={LI_CLS}>{DASH}<span>Point you toward organisations that can actually help — advice lines, solicitors, advocacy, emotional support.</span></li>
              <li className={LI_CLS}>{DASH}<span>Publish families&rsquo; own accounts, with their consent and in their own words, where it is safe and lawful to do so.</span></li>
              <li className={LI_CLS}>{DASH}<span>Argue for a more open system.</span></li>
            </ul>

            <h3 className={H3_CLS}>It does not</h3>
            <ul className={LIST_CLS}>
              <li className={LI_CLS}>{DASH}<span><strong className="text-ink">Give legal advice.</strong> This site is not a substitute for a solicitor and never will be. If you are in or approaching proceedings, get one — legal aid for parents in care proceedings and at the pre-proceedings stage is not means-tested.</span></li>
              <li className={LI_CLS}>{DASH}<span><strong className="text-ink">Advise on individual cases.</strong> There is one person here and no capacity to take on cases. Every enquiry gets pointed toward someone who can help properly.</span></li>
              <li className={LI_CLS}>{DASH}<span><strong className="text-ink">Take a view on whether any particular decision was right.</strong> We do not know your case and cannot know it.</span></li>
              <li className={LI_CLS}>{DASH}<span><strong className="text-ink">Sell anything, or refer you to anyone who will.</strong> Be careful of people who charge for what advice lines and legal aid solicitors provide for free.</span></li>
            </ul>

            <h2 className={H2_CLS}>How this site is written and checked</h2>
            <p className={P_CLS}>
              Everything explaining law or process is written from primary sources — legislation, statutory guidance, the Family Procedure Rules, published judgments — and links to them so you can check it yourself. Each page carries the date it was last reviewed.
            </p>
            <p className={P_CLS}>
              If you find something here that is wrong,{" "}
              <a
                href="mailto:info@truth4parents.com"
                className="text-river underline underline-offset-4 hover:text-ember"
              >
                tell us
              </a>{" "}
              and it will be corrected and the correction noted. That standard is the one this site asks of others, so it applies here first.
            </p>
            <p className={P_CLS}>
              Nothing on this site is written by AI without a human checking it against the source.
            </p>

            <h2 className={H2_CLS}>Coverage</h2>
            <p className={P_CLS}>
              This site covers <strong className="text-ink">England</strong>. Much of the court process is shared with Wales, but the assessment and safeguarding framework in Wales runs on different legislation, and where a page does not apply in Wales it says so. Scotland and Northern Ireland have entirely separate systems and are not covered here — if you are in either, the guidance on this site does not apply to you.
            </p>

            <div
              className="mt-16 p-8 md:p-10 border border-rule bg-linen-alt"
              data-testid="about-contact-block"
            >
              <div className={OVERLINE_CLS}>Get in touch</div>
              <h3 className="font-serif text-2xl md:text-3xl mt-4 text-ink leading-tight tracking-tight">
                One inbox. One person.
              </h3>
              <ul className="mt-6 space-y-3 text-base">
                <li className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
                  <span className="text-ink-muted font-accent text-xs uppercase tracking-wide w-32 flex-shrink-0">
                    Families
                  </span>
                  <a
                    href="mailto:support@truth4parents.com"
                    className="text-river underline underline-offset-4 hover:text-ember font-accent"
                  >
                    support@truth4parents.com
                  </a>
                </li>
                <li className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
                  <span className="text-ink-muted font-accent text-xs uppercase tracking-wide w-32 flex-shrink-0">
                    Press and media
                  </span>
                  <a
                    href="mailto:press@truth4parents.com"
                    className="text-river underline underline-offset-4 hover:text-ember font-accent"
                  >
                    press@truth4parents.com
                  </a>
                </li>
                <li className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
                  <span className="text-ink-muted font-accent text-xs uppercase tracking-wide w-32 flex-shrink-0">
                    Everything else
                  </span>
                  <a
                    href="mailto:info@truth4parents.com"
                    className="text-river underline underline-offset-4 hover:text-ember font-accent"
                  >
                    info@truth4parents.com
                  </a>
                </li>
              </ul>
              <p className="mt-6 text-sm text-ink-muted leading-relaxed">
                This is a one-person site, so replies take time — allow a week.{" "}
                <strong className="text-ink">
                  No inbox here is monitored out of hours or in an emergency.
                </strong>{" "}
                If a child is in immediate danger, call <strong className="text-ink">999</strong>. If you need to talk to someone now, Samaritans are on{" "}
                <strong className="text-ink">116 123</strong>, free, any time.
              </p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
